import { useCallback, useContext } from 'react';
import { t } from '@lingui/macro';

import ReduxStoreContext from '@rover/react-lib/src/context/ReduxStoreContext';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import {
  effects as newsletterSignupEffects,
  selectors as newsletterSignupSelectors,
} from '@rover/react-lib/src/redux/ducks/newsletterSignup.duck';
import {
  ApiNewsletterSignupCreate200Status,
  NewsletterSignup,
  useApiNewsletterSignupCreate,
} from '@rover/rsdk/src/apiClient/latest';

import useDucks from './useDucks';

export type NewsletterSignupStateData = {
  isSuccessful: boolean;
  isLoading: boolean;
  errorMessage?: string | undefined;
};

export type NewsletterSignupData = {
  signupData: NewsletterSignupStateData;
  submitEmail: (data: NewsletterSignup) => void;
};

const selector = (state): NewsletterSignupStateData => ({
  isSuccessful: newsletterSignupSelectors.isSuccessful(state),
  isLoading: newsletterSignupSelectors.isLoading(state),
  errorMessage: newsletterSignupSelectors.errorMessage(state),
});

export const useNewsletterSignupShim = (): NewsletterSignupData => {
  const { isReactQueryRoute } = usePageContext();
  const {
    reduxStore: { dispatch },
  } = useContext(ReduxStoreContext);
  const { mutate, isLoading: isLoadingHook, data } = useApiNewsletterSignupCreate();
  const { data: selectedData } = useDucks({ selector });

  const isSuccessful = isReactQueryRoute
    ? data?.status === ApiNewsletterSignupCreate200Status.success
    : selectedData.isSuccessful === true;

  const isLoading = isReactQueryRoute ? isLoadingHook : selectedData.isLoading === true;

  let errorMessage: string | undefined;
  if (isReactQueryRoute) {
    if (data?.status && data?.status === ApiNewsletterSignupCreate200Status.failure) {
      errorMessage = t`Something went wrong, please try again.`;
    }
  } else {
    errorMessage = selectedData.errorMessage;
  }

  const submitEmail = useCallback(({ email }) => {
    if (isReactQueryRoute) {
      mutate({ data: { email } });
    } else {
      dispatch(newsletterSignupEffects.submitEmail({ email }));
    }
  }, []);

  return {
    signupData: { isSuccessful, isLoading, errorMessage },
    submitEmail,
  };
};
